import {
  Button,
  ProjectionChartSection,
  YieldBearingAssetCompositionRecommendationButton,
  YieldBearingAssetCompositionSection,
  YieldBearingAssetTvlAndReturnCarouselSection,
} from '@/components';
import { AiAlert, InfoAlert } from '@/components/molecules';
import {
  UnderlyingYieldBearingAssetInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import {
  calculateProjectionData,
  calculateProjectionSummary,
  generateWhatThisMeansMessage as generateWhatProjectionMeansMessage,
} from '@/utils/functionalUtils';
import { MESSAGES } from '@/utils/strings';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { YieldBearingAssetDepositFormDataInterface } from './interfaces/YieldBearingAssetActionsInterfaces';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  rootGap: 'gap-sm',
  gapBetweenCompositionChartAndGroupOfButtons: 'gap-sm',
  gapBetweenButtonsInFooter: 'gap-sm',
};
interface Props {
  yieldBearingAsset: YieldBearingAssetInterface;
  customUnderlyingYieldBearingAssets?: UnderlyingYieldBearingAssetInterface[];
  depositData: YieldBearingAssetDepositFormDataInterface;
  handleCompositionConfirm: (
    confirmedUnderlyingYieldBearingAssets: UnderlyingYieldBearingAssetInterface[],
  ) => void;
  showHelp?: boolean;
  showProjectedPerformance?: boolean;
  showHistoricalPerformance?: boolean;
  isOptimizing: boolean;
  onOptimize: (assessment: { question: string; rating: number }[]) => void;
  optimizationExplanation?: string;
}
const YieldBearingAssetInvestmentAllocationConfiguration: FC<Props> = ({
  yieldBearingAsset,
  customUnderlyingYieldBearingAssets,
  depositData,
  handleCompositionConfirm,
  showHelp = false,
  showHistoricalPerformance = false,
  showProjectedPerformance = false,
  isOptimizing = false,
  onOptimize,
  optimizationExplanation = '',
}) => {
  const originalUnderlyingYieldBearingAssets = useMemo(
    () =>
      yieldBearingAsset.underlyingYieldBearingAssets.map((asset) =>
        JSON.parse(JSON.stringify(asset)),
      ),
    [yieldBearingAsset.underlyingYieldBearingAssets],
  );
  const [underlyingYieldBearingAssets, setUnderlyingYieldBearingAssets] =
    useState<UnderlyingYieldBearingAssetInterface[]>(
      customUnderlyingYieldBearingAssets ||
        depositData.underlyingYieldBearingAssetsAllocation?.length > 0
        ? depositData.underlyingYieldBearingAssetsAllocation
        : yieldBearingAsset.underlyingYieldBearingAssets,
    );
  const [projectionData, setProjectionData] = useState<
    { value: number; time: string; label: string }[]
  >([]);
  const [effectiveApy, setEffectiveApy] = useState<number>(0);
  const [optimizationCompleted, setOptimizationCompleted] =
    useState<boolean>(false);
  const handleProjectionCalculation = useCallback(
    (assets: UnderlyingYieldBearingAssetInterface[]) => {
      const selectedAsset = depositData.token;
      const usdPrice = selectedAsset?.priceInUsd || 1;
      const totalAmountInUsd = (depositData?.amount || 100) * usdPrice;
      const { projectionData, effectiveApy } = calculateProjectionData(
        assets,
        totalAmountInUsd,
      );
      setEffectiveApy(effectiveApy);
      setProjectionData(projectionData);
    },
    [depositData],
  );
  useEffect(() => {
    const initialAssets = depositData.underlyingYieldBearingAssetsAllocation
      ?.length
      ? depositData.underlyingYieldBearingAssetsAllocation
      : yieldBearingAsset.underlyingYieldBearingAssets;
    setUnderlyingYieldBearingAssets(initialAssets);
  }, [
    depositData.underlyingYieldBearingAssetsAllocation,
    yieldBearingAsset.underlyingYieldBearingAssets,
  ]);
  useEffect(() => {
    const initialAssets = depositData.underlyingYieldBearingAssetsAllocation
      ?.length
      ? depositData.underlyingYieldBearingAssetsAllocation
      : yieldBearingAsset.underlyingYieldBearingAssets;
    handleProjectionCalculation(initialAssets);
  }, [depositData, yieldBearingAsset, handleProjectionCalculation]);
  useEffect(() => {
    if (
      isOptimizing === false &&
      depositData.underlyingYieldBearingAssetsAllocation?.length > 0
    ) {
      setOptimizationCompleted(true);
    }
  }, [isOptimizing, depositData.underlyingYieldBearingAssetsAllocation]);
  const handleCompositionUpdate = (
    newUnderlyingYieldBearingAssets: UnderlyingYieldBearingAssetInterface[],
  ) => {
    setUnderlyingYieldBearingAssets(newUnderlyingYieldBearingAssets);
    handleProjectionCalculation(newUnderlyingYieldBearingAssets);
  };
  const handleCompositionReset = () => {
    setUnderlyingYieldBearingAssets(() =>
      originalUnderlyingYieldBearingAssets.map((asset) =>
        JSON.parse(JSON.stringify(asset)),
      ),
    );
    handleProjectionCalculation(originalUnderlyingYieldBearingAssets);
    setOptimizationCompleted(false);
  };
  const handleConfirmClick = () => {
    handleCompositionConfirm(underlyingYieldBearingAssets);
  };
  const whatProjectionMeansMessage = useMemo(() => {
    const { startingAmountUSD, endingAmountUSD, totalReturnPercentage } =
      calculateProjectionSummary(
        depositData?.amount || 0,
        depositData.token?.priceInUsd || 1,
        projectionData,
      );
    return generateWhatProjectionMeansMessage(
      endingAmountUSD,
      startingAmountUSD,
      depositData.amount || 0,
      depositData.token?.symbol || 'USD',
      projectionData.length - 1,
      totalReturnPercentage,
    );
  }, [depositData, projectionData]);
  return (
    <div
      className={`flex flex-col w-full h-full items-start justify-stretch ${spacingConfig.rootGap}`}
    >
      {showHelp && (
        <InfoAlert
          title={
            MESSAGES.YIELD_BEARING_ASSET_ACTIONS
              .COMPOSITION_CONFIGURATION_SCREEN.HELP_TITLE
          }
          description={
            MESSAGES.YIELD_BEARING_ASSET_ACTIONS
              .COMPOSITION_CONFIGURATION_SCREEN.HELP_DESCRIPTION
          }
        />
      )}
      {underlyingYieldBearingAssets.length > 1 && (
        <div
          className={`flex flex-col w-full ${spacingConfig.gapBetweenCompositionChartAndGroupOfButtons}`}
        >
          <YieldBearingAssetCompositionSection
            yieldBearingAsset={yieldBearingAsset}
            underlyingYieldBearingAssets={underlyingYieldBearingAssets}
            canChangeComposition
            onCompositionChange={handleCompositionUpdate}
          />
          {showHistoricalPerformance && (
            <YieldBearingAssetTvlAndReturnCarouselSection
              asset={yieldBearingAsset}
              customCompositions={underlyingYieldBearingAssets.map((asset) => ({
                composition: asset.composition,
              }))}
            />
          )}
          {showProjectedPerformance && (
            <ProjectionChartSection
              projectionData={projectionData}
              growthRate={effectiveApy}
              showGrowthRate
              chartId={`${yieldBearingAsset.underlyingYieldBearingAssets[0].symbol}AllocationConfig`}
              extraContent={
                <InfoAlert
                  title="What this means."
                  description={whatProjectionMeansMessage}
                />
              }
              noDataNode={
                <InfoAlert
                  title="No projection data available."
                  description="Please adjust the allocations to generate a projection."
                  className={`${borderClassToVisualizeElementsAndSpacing}`}
                />
              }
            />
          )}
          {optimizationCompleted && !isOptimizing && (
            <AiAlert
              title="Personalized Composition"
              description={
                optimizationExplanation ||
                'Your portfolio has been optimized based on your risk profile. You can now review the allocations and confirm when ready.'
              }
              showChip={optimizationExplanation ? true : false}
              chipText={
                optimizationExplanation ? `AI Recommendation` : undefined
              }
            />
          )}
          <div
            className={`flex flex-1 ${spacingConfig.gapBetweenButtonsInFooter}`}
          >
            <Button fullWidth onClick={handleCompositionReset}>
              Reset
            </Button>
            <YieldBearingAssetCompositionRecommendationButton
              fullWidth
              asset={yieldBearingAsset}
              loading={isOptimizing}
              onOptimize={onOptimize}
            />
            <Button fullWidth onClick={handleConfirmClick}>
              Confirm
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
export default YieldBearingAssetInvestmentAllocationConfiguration;
